import { faArrowsRotate, faPaintBrush } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Group, Image, Skeleton, Stack, Text, Title } from '@mantine/core'
import { FC } from 'react'
import ActiveIcon from '../../assets/statusIcons/Active.svg'
import { TransactionItem } from '../../models/Transactions/TransactionItem'
import { COLORS } from '../../utils/constants'
import TransactionItemCard from './TransactionItemCard'

interface TransactionDetailsProps {
  transactionDetails: {
    orderDate: string
    totalAmount: number
    items?: TransactionItem[]
  } | null
  loading: boolean
}

const LoadingSkeleton = () => (
  <Stack gap='lg' p='xl' pt='sm'>
    {/* Auto Renewals Section */}
    <Group gap='xs' align='center'>
      <Skeleton height={28} width={150} />
    </Group>
    {[1, 2].map((idx) => (
      <Skeleton key={`auto-renewal-${idx}`} height={100} radius='md' />
    ))}

    {/* One Time Purchases Section */}
    <Group gap='xs' align='center' pt='md'>
      <Skeleton height={28} width={180} />
    </Group>
    {[1, 2].map((idx) => (
      <Skeleton key={`one-time-${idx}`} height={100} radius='md' />
    ))}
  </Stack>
)

export const TransactionDetailsComponent: FC<TransactionDetailsProps> = ({
  transactionDetails,
  loading,
}) => {
  if (loading) {
    return <LoadingSkeleton />
  }
  if (!transactionDetails) {
    return <Text color='red'>Unable to load invoice details.</Text>
  }

  const items = transactionDetails.items ?? []
  const autoRenewals = items.filter((item) => item.isAutoRenewal)
  const oneTimePurchases = items.filter(
    (item) => !item.isAutoRenewal && !item.isDesignRequest,
  )
  const designRequests = items.filter((item) => item.isDesignRequest)

  return (
    <Stack gap='lg' p='xl' pt='sm'>
      {autoRenewals.length > 0 && (
        <>
          <Group gap='xs' align='center'>
            <FontAwesomeIcon
              icon={faArrowsRotate}
              size='lg'
              color={COLORS.LightGreen}
            />
            <Title order={3} c={COLORS.LightGreen}>
              Auto Renewals
            </Title>{' '}
          </Group>

          {autoRenewals.map((item, idx) => (
            <TransactionItemCard key={idx} item={item} />
          ))}
        </>
      )}
      {oneTimePurchases.length > 0 && (
        <>
          <Group gap='xs' align='center' pt='md'>
            <Image src={ActiveIcon} width={24} height={24} alt='Active' />{' '}
            <Title order={3} c={COLORS.LightGreen}>
              One Time Purchases
            </Title>
          </Group>
          {oneTimePurchases.map((item, idx) => (
            <TransactionItemCard key={idx} item={item} />
          ))}
        </>
      )}
      {designRequests.length > 0 && (
        <>
          <Group gap='xs' align='center' pt='md'>
            <FontAwesomeIcon
              icon={faPaintBrush}
              size='lg'
              color={COLORS.LightGreen}
            />
            <Title order={3} c={COLORS.LightGreen}>
              Design Requests
            </Title>
          </Group>
          {designRequests.map((item, idx) => (
            <TransactionItemCard key={idx} item={item} />
          ))}
        </>
      )}
      {autoRenewals.length === 0 &&
        oneTimePurchases.length === 0 &&
        designRequests.length === 0 && (
          <Text c='dimmed' ta='center'>
            No items found.
          </Text>
        )}
    </Stack>
  )
}

export default TransactionDetailsComponent
